<template>
  <parent>
    <div
      ref="gjWizard"
      class="gj-wizard"
    >
      <loading-page
        v-if="loading || gettingDatacenters"
        full
      />
      <form-wizard
        v-if="!loading && healthcheck"
        color="#7367F0"
        :title="null"
        :subtitle="null"
        shape="square"
        @on-change="wizardToTop"
        @on-complete="formSubmitted"
      >
        <!-- REQUEST FORM -->
        <tab-content
          title="Request"
          class="container col-lg-9"
          :before-change="validationStep1"
        >
          <validation-observer
            ref="healthcheckStep1"
            v-slot="{ errors }"
          >
            <paper-simple>
              <custom-input-radio
                v-model="healthcheck.protocol"
                label="Protocol"
                description-class="pl-0"
                center
                md-cols="6"
                :options="getOptions(healthcheck.protocolOptions)"
              />
              <input-field
                class="mb-2"
                label="Request Method & Request URL*"
                label-position="top"
                :error="errors.url ? errors.url[0] : null"
              >
                <custom-select
                  v-model="healthcheck.method"
                  class="r-dropdown col-2 p-0 method-dropdown"
                  :options="getOptions(healthcheck.methodOptions)"
                />
                <validation-provider
                  #default="{ errors }"
                  name="URL"
                  rules="required|notLocalhost"
                  tag="div"
                  class="w-100"
                >
                  <div class="d-flex">
                    <div class="protocol-split">
                      {{
                        healthcheck.protocol == 0 ? 'https://' : 'http://'
                      }}
                    </div>
                    <b-form-input
                      v-model="formatUrl"
                      placeholder="example.com/"
                      class="healthcheck-url"
                      :state="errors.length > 0 ? false : null"
                    />
                  </div>
                </validation-provider>
              </input-field>
            </paper-simple>
            <paper-simple>
              <validation-provider
                #default="{ errors }"
                name="Datacenter"
                tag="div"
                rules="required"
              >
                <input-field
                  label="Datacenter*"
                  label-position="top"
                  :error="errors.length ? errors[0] : null"
                >
                  <datacenter-checkbox
                    v-model="dataCenters"
                    class="w-100"
                  />
                </input-field>
              </validation-provider>
            </paper-simple>
            <paper-simple>
              <b-tabs
                content-class="pt-1"
                fill
              >
                <b-tab
                  active
                  title="Headers"
                >
                  <form-headers
                    :healthcheck="healthcheck"
                    :headers="headers"
                    :editable="true"
                    @pushHeaders="overwriteHeaders"
                    @deleteHeader="removeHeader"
                    @headersUpdated="checkHeaders"
                  />
                </b-tab>
                <b-tab title="Request Body">
                  <form-body-editor
                    ref="requestBodyEditor"
                    editor-id="request-editor-details"
                    :request-method="healthcheck.method"
                    :body-value="healthcheck.body"
                    :editable="true"
                    :content-type="contentType"
                    editor-title="Request Body:"
                  />
                </b-tab>
                <b-tab title="Settings">
                  <request-settings
                    :healthcheck="healthcheck"
                    :state="healthcheckInitialState || false"
                    :editable="true"
                  />
                </b-tab>
              </b-tabs>
            </paper-simple>
          </validation-observer>
        </tab-content>
        <!-- RESPONSE & SETTINGS FORM -->
        <tab-content
          title="Response & Settings"
          class="container col-lg-9"
          :before-change="validationStep2"
        >
          <validation-observer ref="healthcheckStep2">
            <paper-simple>
              <div class="row">
                <input-field
                  label="Healthcheck Group"
                  label-position="top"
                  class="col-12 col-md-6"
                >
                  <group-options
                    v-model="healthcheck.groupId"
                    :selected-group-id="healthcheck.groupId || null"
                    class="w-100"
                  />
                </input-field>
                <validation-provider
                  #default="{ errors }"
                  name="Name"
                  tag="div"
                  class="col-12 col-md-6"
                  rules="required|min:3|max:100"
                >
                  <input-field
                    label="Name*"
                    label-position="top"
                    :error="errors.length ? errors[0] : null"
                  >
                    <b-form-input
                      v-model.trim="healthcheck.name"
                      :state="errors.length > 0 ? false : null"
                    />
                  </input-field>
                </validation-provider>
              </div>
              <input-field
                label="Healthchecking Status"
                :info="messages.healthcheckRequest"
                label-position="top"
                info-position="label"
                class="worker-status mb-2"
                @openInfo="
                  showInfo(
                    messages.healthcheckWorkerStatus.title,
                    messages.healthcheckWorkerStatus.message,
                  )
                "
              >
                <b-form-checkbox
                  v-model="healthcheck.state"
                  name="check-button"
                  class="status-button"
                  switch
                  inline
                >
                  {{ healthcheck.state == true ? 'Active' : 'Disabled' }}
                </b-form-checkbox>
              </input-field>
              <validation-provider
                #default="{ errors }"
                name="Expected Response"
                rules="required"
              >
                <input-field
                  label="Expected Response*"
                  class="mr-35"
                  label-position="top"
                  :error="errors.length ? errors[0] : null"
                >
                  <v-select
                    v-model="expectedResponseOption"
                    :state="errors.length > 0 ? false : null"
                    class="flex-fill h-100 expected-response-select"
                    :options="expectedResponseOptions"
                    placeholder="Select Request Method"
                  />
                </input-field>
              </validation-provider>
              <custom-alert
                class="mr-35"
                items-center
                icon-name="InfoAlt"
              >
                The HTTP status code to that the probe expects. The default
                value is 200.
              </custom-alert>
              <div class="d-flex flex-wrap">
                <input-field
                  v-if="!details"
                  class="col-12 col-md-3"
                  label="Check frequency"
                  label-position="top"
                >
                  <b-form-radio-group
                    v-model="frequency"
                    :options="frequencyOptions"
                    stacked
                    @change="changeFrequency"
                  />
                </input-field>
                <div
                  class="d-block col-12 p-0"
                  :class="!details && 'col-md-9'"
                >
                  <div class="col-12 d-flex flex-wrap p-0">
                    <validation-provider
                      #default="{ errors }"
                      name="schedule"
                      rules="required"
                      tag="div"
                      class="mb-2 p-0 schedule-options"
                      :class="
                        healthcheck.scheduleType === 0 ||
                          healthcheck.scheduleType === 3
                          ? 'col-12'
                          : 'col-md-6'
                      "
                    >
                      <label
                        for="interval"
                        class="labelMargin"
                      >Schedule</label>
                      <custom-select
                        v-model="healthcheck.scheduleType"
                        :state="errors.length > 0 ? false : null"
                        class="r-dropdown p-0"
                        :options="getOptions(healthcheck.scheduleTypeOptions)"
                      />
                      <span class="errors-text text-danger">
                        {{ errors.length > 0 ? errors[0] : '' }}
                      </span>
                    </validation-provider>
                    <validation-provider
                      v-if="
                        healthcheck.scheduleType !== 0 &&
                          healthcheck.scheduleType !== 3
                      "
                      #default="{ errors }"
                      name="interval"
                      rules="required|numberInt|minValueRule:1"
                      tag="div"
                      class="col-12 col-md-6 p-0 pl-md-1 w-100"
                    >
                      <label
                        for="interval"
                        class="labelMargin"
                      >Every*</label>
                      <b-form-input
                        v-model="healthcheck.interval"
                        :disabled="
                          (!details && frequency !== 3) ||
                            healthcheck.state == false
                        "
                        :state="errors.length > 0 ? false : null"
                        class="m-0"
                      />
                      <span class="errors-text text-danger">
                        {{ errors.length > 0 ? errors[0] : '' }}
                      </span>
                    </validation-provider>
                  </div>
                  <div class="col-12 p-0">
                    <validation-provider
                      #default="{ errors }"
                      name="timeout"
                      rules="required|numberInt"
                      tag="div"
                      class="w-100"
                    >
                      <label
                        class="labelMargin"
                        for="timeout"
                      >Timeout* (ms)</label>
                      <b-form-input
                        v-model="healthcheck.timeout"
                        class="m-0"
                        :disabled="
                          (!details && frequency !== 3) ||
                            healthcheck.state == false
                        "
                        :state="errors.length > 0 ? false : null"
                      />
                      <span class="errors-text text-danger">
                        {{ errors.length > 0 ? errors[0] : '' }}
                      </span>
                    </validation-provider>
                  </div>
                  <validation-provider
                    #default="{ errors }"
                    name="Retries"
                    tag="div"
                    class="col-12 p-0"
                    rules="required|between:1,5"
                  >
                    <input-field
                      label="Retries*"
                      label-position="top"
                      :error="errors.length ? errors[0] : null"
                    >
                      <b-form-input
                        v-model.trim="healthcheck.retries"
                        :state="errors.length > 0 ? false : null"
                      />
                    </input-field>
                  </validation-provider>
                </div>
              </div>
              <input-field
                label="Body Validation"
                :info="messages.healthcheckRequest"
                label-position="top"
                info-position="label"
                class="worker-status mb-2"
                @openInfo="
                  showInfo(
                    messages.healthcheckWorkerStatus.title,
                    messages.healthcheckWorkerStatus.message,
                  )
                "
              >
                <b-form-checkbox
                  v-model="customBodyValidation"
                  name="check-button"
                  class="status-button"
                  switch
                  inline
                >
                  {{ customBodyValidation ? 'Validate Body' : 'None' }}
                </b-form-checkbox>
              </input-field>
              <custom-input-radio
                v-if="customBodyValidation"
                v-model="customBodyType"
                description-class="pl-0"
                center
                md-cols="6"
                :options="getOptions(expectedValidationTypes)"
              />
              <form-body-editor
                v-if="customBodyValidation"
                ref="expectedBodyEditor"
                editor-id="expected-body-details"
                :body-value="healthcheck.expectedBody"
                :editable="true"
                :content-type="contentType"
                class="mt-2"
                editor-title="Expected Response:"
                :changeable-language="true"
              />
            </paper-simple>
          </validation-observer>
        </tab-content>
        <template
          slot="footer"
          slot-scope="props"
        >
          <div class="wizard-custom-footer col-12 col-lg-9">
            <b-button
              variant="bg-white"
              class="mr-1"
              :class="props.activeTabIndex > 0 ? 'transparent' : ''"
              @click="onCancel()"
            >
              Cancel
            </b-button>
            <b-button
              v-if="props.activeTabIndex > 0"
              variant="bg-white"
              class="mr-1"
              @click="props.prevTab()"
            >
              Go Back
            </b-button>
            <b-button
              v-if="!props.isLastStep"
              class="wizard-footer-right"
              variant="bg-primary"
              @click="
                props.nextTab()
                preapareFormSubmit()
              "
            >
              Next
            </b-button>
            <button-loading
              v-else
              class="wizard-footer-right done-button"
              :spinning="updatingHealthcheck || addingHealthcheck"
              :disabled="updatingHealthcheck || addingHealthcheck"
              variant="bg-primary"
              @click="validationStep2()"
            >Save</button-loading>
          </div>
        </template>
      </form-wizard>
      <info
        v-if="openInfo"
        :title="infoTitle"
        :message="infoMessage"
        @close="openInfo = false"
      />
    </div>
  </parent>
</template>
<script>
import { FormWizard, TabContent } from 'vue-form-wizard'
import 'vue-form-wizard/dist/vue-form-wizard.min.css'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  required,
  numberInt,
  minValueRule,
  minIntValue,
  maxIntValue,
} from '@validations'
import redirectError from '@/services/redirectError'
import messages from '../../cdn/messages'
import vSelect from 'vue-select'
import { BTabs, BTab } from 'bootstrap-vue'
import CustomInputRadio from '@/components/form/CustomInputRadio.vue'
import FormHeaders from './components/FormHeaders.vue'
import FormBodyEditor from './components/FormBodyEditor.vue'
import RequestSettings from './components/RequestSettings.vue'
import GroupOptions from '../components/GroupOptions.vue'
import DatacenterCheckbox from './components/DatacenterCheckbox.vue'

export default {
  name: 'HealthcheckForm',
  components: {
    FormWizard,
    CustomInputRadio,
    FormHeaders,
    FormBodyEditor,
    RequestSettings,
    TabContent,
    ValidationProvider,
    ValidationObserver,
    vSelect,
    BTabs,
    BTab,
    GroupOptions,
    DatacenterCheckbox,
  },
  props: {
    details: {
      type: Boolean,
      default: false,
    },
    cloned: {
      type: Boolean,
      default: false,
    },
    healthcheckId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      healthcheckInitialState: false,
      required,
      numberInt,
      minValueRule,
      minIntValue,
      maxIntValue,
      selected: [],
      messages,
      openInfo: false,
      infoTitle: '',
      infoMessage: '',
      headers: [],
      formattedHeaders: {},
      frequency: 1,
      customBodyValidation: false,
      frequencyOptions: [
        {
          text: 'Low',
          value: 0,
        },
        {
          text: 'Medium',
          value: 1,
        },
        {
          text: 'High',
          value: 2,
        },
        {
          text: 'Custom',
          value: 3,
        },
      ],
      timeout: 5000,
      timeouts: [2000, 5000, 10000],
      intervals: [5, 2, 1],
      expectedResponseOption: null,
      groupId: 0,
      dataCenters: [],
      contentType: 'json',
      customBodyType: 1,
      expectedValidationTypes: [
        {
          id: 0,
          value: 'Match Whole Body',
        },
        {
          id: 1,
          value: 'Contains Body',
        },
      ],
    }
  },
  computed: {
    formatUrl: {
      get() {
        return this.healthcheck.url && this.healthcheck.url !== ''
          ? this.healthcheck.url.replace(/^https?:\/\/?/i, '')
          : ''
      },
      set(val) {
        this.healthcheck.url = val.replace(/^https?:\/\//i, '')
      },
    },
    healthcheck() {
      return this.$store.state.healthchecks.healthcheck
    },
    loading() {
      return this.$store.state.healthchecks.gettingHealthcheck
    },
    gettingDatacenters() {
      return this.$store.state.healthchecks.gettingDatacenters
    },
    updatingHealthcheck() {
      return this.$store.state.healthchecks.updatingHealthcheck
    },
    addingHealthcheck() {
      return this.$store.state.healthchecks.addingHealthcheck
    },
    expectedResponseOptions() {
      return this.healthcheck
        ? this.healthcheck.httpStatusCodes.map(el => ({
          value: el.id,
          label: `${el.id}: ${el.value}`,
        }))
        : []
    },
    expectedResponseLabel() {
      return this.expectedResponseOptions.find(
        el => el.value === this.healthcheck.expectedResponse,
      )?.label
    },
  },
  watch: {
    headers() {
      this.checkHeaders()
    },
  },
  created() {
    this.$store
      .dispatch(
        'healthchecks/getHealthcheck',
        this.details ? this.healthcheckId : 0,
      )
      .then(() => {
        this.expectedResponseOption = {
          value: this.healthcheck.expectedResponse,
          label: this.expectedResponseLabel,
        }
        if (this.healthcheck.expectedBody !== null) {
          this.customBodyType = this.healthcheck.matchWholeBody ? 0 : 1
          this.customBodyValidation = true
        }
        if (this.cloned) {
          this.healthcheck.name = `${this.healthcheck.name} - cloned`
        }
        if (this.details && !this.cloned) {
          this.healthcheckInitialState = this.healthcheck.state
          this.dataCenters = this.healthcheck.healthcheckLogStatuses.map(({ dataCenterId }) => dataCenterId)
        } else {
          this.healthcheckInitialState = false
        }
        this.headers.sort((x, y) => y.isDefault - x.isDefault)
      })
      .catch(err => {
        redirectError(err)
      })
    this.$store.dispatch('healthchecks/fetchDatacenters')
      .catch(err => {
        this.errorToast(err.response?.data?.errors[0] || 'Something went wrong while fetching datacenters.')
      })
  },
  methods: {
    validationStep1() {
      return new Promise((resolve, reject) => {
        if (this.healthcheck.method === 0 || this.healthcheck.method === 1) {
          this.healthcheck.body = null
        } else {
          const getValue = this.$refs.requestBodyEditor.editor.getValue() || '{}'
          if (this.contentType.includes('json')) {
            try {
              JSON.parse(getValue)
            } catch (error) {
              this.errorToast('Body is not valid JSON!')
              reject()
            }
          }
          this.healthcheck.body = getValue
        }
        this.$refs.healthcheckStep1.validate().then(success => {
          if (success) {
            resolve(true)
          } else {
            this.$refs.gjWizard.scroll({ top: 150, behavior: 'smooth' })
            reject()
          }
        })
      })
    },
    validationStep2() {
      return new Promise((resolve, reject) => {
        if (this.customBodyValidation) {
          const customBodyValue = this.$refs.expectedBodyEditor.editor.getValue() || '{}'
          this.healthcheck.expectedBody = customBodyValue
        } else {
          this.healthcheck.expectedBody = null
        }
        this.$refs.healthcheckStep2.validate().then(success => {
          if (success) {
            this.formSubmitted()
            resolve(true)
          } else {
            this.errorToast('Check if all fields are valid!')
            reject()
          }
        })
      })
    },
    formatHeaders() {
      this.formattedHeaders = {}
      const validateHeader = '<calculated when request is sent>'
      // eslint-disable-next-line no-restricted-syntax
      for (const i in this.headers) {
        if (
          this.headers[i].key
          && (this.headers[i].isDefault || this.headers[i].value)
        ) {
          const tempKey = this.headers[i].key
          const tempValue = {
            value:
              this.headers[i].value === validateHeader
                ? null
                : this.headers[i].value,
            state: this.headers[i].state,
            isDefault: this.headers[i].isDefault,
          }
          const tempHeader = {
            [tempKey]: tempValue,
          }
          Object.assign(this.formattedHeaders, tempHeader)
        }
      }
      this.healthcheck.headers = this.formattedHeaders
    },
    removeHeader(item) {
      this.headers.splice(this.headers.indexOf(item), 1)
    },
    overwriteHeaders(headers) {
      this.headers.push(headers)
    },
    replaceUrl() {
      // eslint-disable-next-line no-unused-expressions
      this.healthcheck.url
        ? this.healthcheck.url.replace(/^https?:\/\//i, '')
        : ''
    },
    preapareFormSubmit() {
      this.replaceUrl()
      this.formatHeaders()
    },
    onCancel() {
      if (this.details && !this.cloned) {
        return this.$router.push({ name: 'healthchecks-details' })
      }
      return this.$router.push({ name: 'healthchecks' })
    },
    formSubmitted() {
      this.healthcheck.expectedResponse = this.expectedResponseOption.value
      this.healthcheck.dataCenters = this.dataCenters
      this.healthcheck.matchWholeBody = this.customBodyType === 0
      this.$emit('submit', this.healthcheck)
    },
    showInfo(title, info) {
      this.openInfo = true
      this.infoTitle = title
      this.infoMessage = info
    },
    changeFrequency(val) {
      if (val !== 3) {
        this.healthcheck.interval = this.intervals[val]
        this.healthcheck.timeout = this.timeouts[val]
        this.healthcheck.scheduleType = 1
      }
    },
    checkHeaders() {
      const index = this.headers.map(e => e.key?.toLowerCase()).lastIndexOf('content-type')
      this.contentType = this.headers[index]?.value || 'json'
    },
    formatString(value) {
      if (typeof value === 'object') {
        const temp = JSON.stringify(value)
        return temp
      }
      return value
    },
  },
}
</script>
<style lang="scss" scoped>
.healthchecks-form-wrapper {
  ::v-deep {
    .form-wrapper {
      &__body {
        > div {
          margin: auto;
        }
      }
    }
  }
  .mr-35 {
    margin-right: 35px;
  }
  .r-dropdown {
    width: 200px !important;
  }
}
.expected-response-select {
  ::v-deep {
    .vs__dropdown-toggle {
      min-height: 42px;
      padding-bottom: 0;
      .vs__selected {
        margin-top: 0;
        height: 100%;
      }
    }
  }
}
.input-field-no-label {
  ::v-deep {
    .input-field-label {
      min-height: 0;
      display: none !important;
    }
    .info-assistant {
      margin-top: 1.5rem;
    }
  }
}
.worker-status {
  ::v-deep {
    .labelMargin {
      margin-bottom: 1.5rem;
    }
    .status-button {
      min-width: 103px;
      user-select: none;
    }
  }
}
.protocol-split {
  display: flex;
  min-width: 72px;
  padding: 0 15px;
  vertical-align: center;
  align-items: center;
  border-top: 1px solid #e0e5eb;
  border-bottom: 1px solid #e0e5eb;
}
.method-dropdown {
  ::v-deep {
    .dropdown-toggle {
      border-radius: 5px 0px 0px 5px !important;
    }
  }
}
.healthcheck-url {
  border-radius: 0px 5px 5px 0px;
}
.errors-text {
  padding-left: 5px;
}
</style>
